import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Shared arrow SVGs for consistent styling
const prevArrow = '<svg class="stroke-current" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.25 6L9 12.25L15.25 18.5" stroke="" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
const nextArrow = '<svg class="stroke-current" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.75 19L15 12.75L8.75 6.5" stroke="" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'

/**
 * Datepicker Stimulus Controller
 *
 * Wraps Flatpickr with proper lifecycle management for Turbo compatibility.
 *
 * Usage:
 *   Range picker:  data-controller="datepicker" data-datepicker-mode-value="range"
 *   Single picker: data-controller="datepicker" data-datepicker-mode-value="single"
 *   Form picker:   data-controller="datepicker" data-datepicker-mode-value="form"
 *
 * Optional:
 *   data-datepicker-class-value="custom-class" - adds class to calendar container
 *   data-datepicker-default-value="2024-01-15" - sets initial date
 *
 * Events:
 *   Dispatches 'datepicker:change' with { detail: { dateStr, selectedDates } }
 *   on date selection, allowing Alpine to listen via @datepicker:change
 */
export default class extends Controller {
  static values = {
    mode: { type: String, default: "form" },  // "range", "single", or "form"
    class: String,  // Optional custom class for calendar container
    default: String  // Optional default date
  }

  connect() {
    // Guard against re-initialization
    // Flatpickr with altInput modifies DOM which can trigger Stimulus reconnect
    // Check if element already has a fully-initialized Flatpickr instance
    // We check for calendarContainer which is only present after full initialization
    const existingFp = this.element._flatpickr
    if (existingFp && existingFp.calendarContainer) {
      // Store reference to existing instance for disconnect cleanup
      this.picker = existingFp
      return
    }

    const config = this.buildConfig()
    this.picker = flatpickr(this.element, config)
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
      this.picker = null
    }
  }

  buildConfig() {
    const baseConfig = {
      static: true,
      monthSelectorType: "static",
      prevArrow,
      nextArrow,
      onChange: (selectedDates, dateStr, instance) => {
        // Dispatch custom event for Alpine integration
        this.element.dispatchEvent(new CustomEvent('datepicker:change', {
          bubbles: true,
          detail: { dateStr, selectedDates }
        }))
      }
    }

    // Add default date if specified
    if (this.hasDefaultValue && this.defaultValue) {
      baseConfig.defaultDate = this.defaultValue
    }

    switch (this.modeValue) {
      case "range":
        return {
          ...baseConfig,
          mode: "range",
          dateFormat: "M j, Y",
          defaultDate: baseConfig.defaultDate || [new Date().setDate(new Date().getDate() - 6), new Date()],
          onChange: (selectedDates, dateStr, instance) => {
            instance.element.value = dateStr.replace("to", "-")
            // Dispatch custom event for Alpine integration
            this.element.dispatchEvent(new CustomEvent('datepicker:change', {
              bubbles: true,
              detail: { dateStr, selectedDates }
            }))
          }
        }

      case "single":
        return {
          ...baseConfig,
          dateFormat: "M j, Y"
        }

      case "form":
      default:
        // Note: We intentionally avoid altInput: true here because it creates
        // a new DOM element which triggers Stimulus reconnection loops.
        // Using UK-friendly format "d M Y" (e.g., "15 Jan 2024") which Rails
        // can parse correctly, while avoiding DOM mutations.
        return {
          ...baseConfig,
          dateFormat: "d M Y",
          onReady: (selectedDates, dateStr, instance) => {
            if (this.hasClassValue) {
              instance.calendarContainer.classList.add(this.classValue)
            }
          }
        }
    }
  }
}
